/**
 * Vendor-neutral name → code matching for post-OCR resolution.
 *
 * Vision extraction returns human-readable *names* ("Contract / Warranty") that
 * rarely match a court's exact policy label ("Contract: Breach Cont/Warranty
 * (06)"). This pure matcher links an extracted name to a code option. Shared by
 * the server resolver (`code-resolver.ts`) and the wizard's inline Step-1
 * selector so both layers resolve identically — no drift.
 *
 * Conservative on purpose: a wrong code mis-files, so anything short of a
 * confident match is left unresolved for the user/operator to pick. The three
 * tiers, in order:
 *   1. exact      — normalized strings equal.
 *   2. fuzzy      — high token-Jaccard overlap (≥ threshold).
 *   3. subset     — every token of the (multi-token) extracted name appears in
 *                   exactly one decisive option. Catches paraphrases that drop
 *                   words ("Contract / Warranty" ⊂ "Contract: Breach
 *                   Cont/Warranty (06)") without matching short/ambiguous names.
 *
 * Deliberately NO substring containment — it let a lone "Contract" match
 * "Contract: Breach…" by mere prefix. The token-subset tier covers the real
 * paraphrase cases with a uniqueness guard instead.
 *
 * `matchNameToCodeAll` is the sibling export that reports the tie
 * `matchNameToCode` rejects: it returns every top-ranked subset-tier match
 * instead of collapsing an ambiguous set to null, so a caller that can ask the
 * user (or narrow by other means) can see the options rather than nothing.
 */
/**
 * Lowercase, strip accents + punctuation, collapse whitespace. NFKD splits an
 * accented letter into base + combining mark; we drop the mark explicitly (so
 * "ó" → "o", not "o ") before collapsing remaining punctuation to spaces —
 * giving "Citación" and "Citacion" an identical normal form.
 */
export function normalizeName(s) {
    return s
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '') // drop combining diacritical marks
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}
function tokenize(s) {
    return new Set(normalizeName(s).split(' ').filter(Boolean));
}
/** Jaccard overlap of token sets — symmetric, 0..1. */
function jaccard(a, b) {
    if (a.size === 0 || b.size === 0)
        return 0;
    let inter = 0;
    for (const t of a)
        if (b.has(t))
            inter++;
    return inter / (a.size + b.size - inter);
}
function isSubset(small, big) {
    for (const t of small)
        if (!big.has(t))
            return false;
    return true;
}
// Fuzzy acceptance threshold (token Jaccard). Tuned to avoid false positives;
// raise if mis-resolution shows up.
const FUZZY_JACCARD_MIN = 0.7;
/**
 * The subset tier's ranking, in one place: every option whose tokens contain
 * all of `targetTokens`, kept only at the single highest Jaccard score, in the
 * order the options were supplied.
 *
 * Both entry points read the tier from here — `matchNameToCode` resolves iff
 * this returns exactly one option, `matchNameToCodeAll` returns the whole set —
 * so the two can never disagree about what "top-ranked" means. Callers gate on
 * `targetTokens.size >= 2` themselves (an empty target subsets everything).
 */
function topRankedSupersets(targetTokens, options) {
    const supersets = [];
    for (const opt of options) {
        const optTokens = tokenize(opt.name);
        if (isSubset(targetTokens, optTokens)) {
            supersets.push({ opt, j: jaccard(targetTokens, optTokens) });
        }
    }
    if (supersets.length === 0)
        return [];
    // Folded rather than Math.max(...) — the option list is caller-supplied and a
    // spread of it would be an unbounded argument list.
    let top = supersets[0].j;
    for (const { j } of supersets)
        if (j > top)
            top = j;
    // The equality below compares each score against a value drawn from this same
    // array, so it is a float compared with itself — no recomputed score to drift.
    return supersets.filter(({ j }) => j === top).map(({ opt }) => opt);
}
/**
 * Best confident match for `name` among `options`, or null.
 *
 * Exact-normalized equality wins outright. Otherwise the highest-scoring fuzzy
 * candidate above threshold is taken. Failing that, the token-subset fallback
 * fires only when the extracted name has ≥2 tokens AND exactly one option is a
 * strictly-tightest superset of them — so a paraphrase that drops words still
 * resolves, but an ambiguous or single-token name does not.
 */
export function matchNameToCode(name, options) {
    const target = normalizeName(name);
    if (!target)
        return null;
    const targetTokens = tokenize(name);
    let best = null;
    for (const opt of options) {
        const candNorm = normalizeName(opt.name);
        if (!candNorm)
            continue;
        if (candNorm === target) {
            return { code: opt.code, name: opt.name, method: 'exact' };
        }
        const j = jaccard(targetTokens, tokenize(opt.name));
        if (j >= FUZZY_JACCARD_MIN && (!best || j > best.score)) {
            best = { opt, score: j };
        }
    }
    if (best)
        return { code: best.opt.code, name: best.opt.name, method: 'fuzzy' };
    // Token-subset fallback: every extracted token present in the option. Needs
    // ≥2 tokens (a lone "complaint" would subset-match many) and a decisive
    // single winner — the tightest superset (highest Jaccard), strictly ahead of
    // the runner-up. Otherwise it's ambiguous and we leave it unresolved.
    if (targetTokens.size >= 2) {
        const top = topRankedSupersets(targetTokens, options);
        if (top.length === 1) {
            return { code: top[0].code, name: top[0].name, method: 'fuzzy' };
        }
    }
    return null;
}
/**
 * Every top-ranked token-subset match for `name` among `options`, in the order
 * the options were supplied.
 *
 * This is the set `matchNameToCode` computes and then discards when more than
 * one option ties for the tightest superset — the tie it reports as
 * unresolved. Rejecting that tie is correct and unchanged; this export just
 * makes the tied set visible so a caller can route around it. A one-element
 * result is exactly the case `matchNameToCode` resolves.
 *
 * Returns [] when the normalized name has fewer than two tokens (the tier's
 * own guard — a lone token subsets too much to mean anything) or when no
 * option is a superset.
 */
export function matchNameToCodeAll(name, options) {
    const targetTokens = tokenize(name);
    if (targetTokens.size < 2)
        return [];
    return topRankedSupersets(targetTokens, options);
}
